import { formatCurrency, formatDateShort } from "../utils/formatters.js";
import { googleSheetsService } from "../services/googleSheets.js";
import { calculateSummary, sortByDate } from "../services/dataProcessor.js";

/**
 * Dashboard Component
 * Renders the main dashboard view with Liquid Glass summary cards and transaction list
 */
export class Dashboard {
  constructor() {
    this.data = null;
    // Pagination settings
    this.pageSize = 50; // Number of transactions to show per page
    this.currentPage = 1;
  }

  /**
   * Load dashboard data with filters
   */
  async loadData(filters = {}) {
    try {
      this.data = await googleSheetsService.getDashboardData(filters);
      // Reset pagination when new data loads
      this.currentPage = 1;
      return this.data;
    } catch (error) {
      console.error("Error loading dashboard data:", error);
      throw error;
    }
  }

  /**
   * Get paginated transactions
   */
  getPaginatedTransactions() {
    if (!this.data || !this.data.transactions) return [];
    const sorted = sortByDate(this.data.transactions, "desc");
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    return sorted.slice(start, end);
  }

  /**
   * Get total number of pages
   */
  getTotalPages() {
    if (!this.data || !this.data.transactions) return 1;
    return Math.ceil(this.data.transactions.length / this.pageSize);
  }

  /**
   * Check if there are more pages
   */
  hasMorePages() {
    return this.currentPage < this.getTotalPages();
  }

  /**
   * Load more transactions (next page)
   */
  loadMore() {
    if (this.hasMorePages()) {
      this.currentPage++;
    }
  }

  /**
   * Render summary cards HTML
   */
  renderSummary() {
    if (!this.data) return "";

    const summary =
      this.data.summary || calculateSummary(this.data.transactions || []);

    const hasForeignCurrency = (this.data.transactions || []).some(
      (t) => t.currency && t.currency !== "₭",
    );

    const foreignNote = hasForeignCurrency
      ? `<div style="text-align:center; margin-bottom:1rem;">
           <span style="display:inline-block; font-size:0.8rem; color:var(--text-secondary); background:rgba(251,191,36,0.12); border-radius:10px; padding:0.3rem 1rem;">
             💱 ຍອດລວມຄຳນວນເປັນກີບ (₭) ໂດຍໃຊ້ອັດຕາແລກປ່ຽນ
           </span>
         </div>`
      : "";

    return `
      ${foreignNote}
      <div class="summary-grid">
        <div class="summary-card" style="--card-accent: var(--primary-green); --icon-bg-rgb: 16, 185, 129;">
          <div class="summary-card-header">
            <span class="summary-card-title">ລາຍຮັບລວມ</span>
            <div class="summary-card-icon">💰</div>
          </div>
          <div class="summary-card-value" style="color: var(--primary-green-dark);">${formatCurrency(summary.totalIncome)}</div>
          <div class="summary-card-label">ລາຍຮັບທັງໝົດໃນໄລຍະນີ້</div>
        </div>
        <div class="summary-card" style="--card-accent: var(--accent-red); --icon-bg-rgb: 239, 68, 68;">
          <div class="summary-card-header">
            <span class="summary-card-title">ລາຍຈ່າຍລວມ</span>
            <div class="summary-card-icon">📉</div>
          </div>
          <div class="summary-card-value" style="color: var(--accent-red);">${formatCurrency(summary.totalExpense)}</div>
          <div class="summary-card-label">ລາຍຈ່າຍທັງໝົດໃນໄລຍະນີ້</div>
        </div>
        <div class="summary-card" style="--card-accent: var(--accent-blue); --icon-bg-rgb: 59, 130, 246;">
          <div class="summary-card-header">
            <span class="summary-card-title">ຍອດຄົງເຫຼືອ</span>
            <div class="summary-card-icon">⚖️</div>
          </div>
          <div class="summary-card-value" style="color: var(--accent-blue);">${formatCurrency(summary.netBalance)}</div>
          <div class="summary-card-label">ເງິນສົດໝູນວຽນສຸດທິ</div>
        </div>
      </div>
    `;
  }

  /**
   * Render transaction list HTML with pagination
   */
  renderTransactionList() {
    if (
      !this.data ||
      !this.data.transactions ||
      this.data.transactions.length === 0
    ) {
      return `
        <div class="glass-panel empty-state" style="text-align: center; padding: 4rem 2rem;">
          <p style="font-size: 4rem; margin-bottom: 1rem;">🔎</p>
          <h3 style="font-weight: 800; color: var(--text-primary); margin-bottom: 0.5rem;">ບໍ່ພົບລາຍການຂໍ້ມູນ</h3>
          <p style="color: var(--text-secondary);">ລອງປ່ຽນຕົວກອງ ຫຼື ວັນທີເພື່ອເບິ່ງຂໍ້ມູນອື່ນ</p>
        </div>
      `;
    }

    // Get paginated transactions
    const paginatedTransactions = this.getPaginatedTransactions();
    const totalTransactions = this.data.transactions.length;
    const totalPages = this.getTotalPages();
    const hasMore = this.hasMorePages();

    // Only apply staggered animation delay to first page items (no delay for loaded more items)
    const transactionItems = paginatedTransactions
      .map((transaction, index) => {
        const isIncome =
          transaction.type === "ລາຍຮັບ" || transaction.type === "income";
        const amountClass = isIncome ? "income" : "expense";
        const amountPrefix = isIncome ? "+" : "-";

        // Staggered animation for first page only
        const isFirstPage = this.currentPage === 1;
        const delay = isFirstPage ? index * 0.03 : 0;
        const animationStyle = isFirstPage
          ? `animation: fadeInUp 0.4s ease backwards ${delay}s;`
          : "";

        const projectBadge = transaction.project
          ? `<span class="transaction-badge" style="background: rgba(59,130,246,0.1); color: #2563eb; border: 1px solid rgba(59,130,246,0.2);">📁 ${transaction.project}</span>`
          : "";

        const documentLinks = transaction.documentLink
          ? transaction.documentLink
              .split(",")
              .map(
                (url) =>
                  `<a href="${url.trim()}" target="_blank" class="btn btn-secondary" style="padding: 6px 12px; font-size: 0.8rem; border-radius: 10px;" title="ເບິ່ງເອກະສານ">📎 ເອກະສານ</a>`,
              )
              .join("")
          : "";

        // Display original amount; if foreign currency, also show exchange rate + Kip equivalent
        const rawAmount = Math.abs(parseFloat(transaction.amount) || 0);
        const currency = transaction.currency || "₭";
        const exchangeRate = parseFloat(transaction.exchangeRate) || 1;
        const isForeign = currency !== "₭";
        const amountDisplay = `${amountPrefix}${formatCurrency(rawAmount, currency)}`;
        const kipEquivalent = isForeign
          ? `<span style="font-size: 0.78rem; color: var(--text-secondary); display: block;">≈ ${formatCurrency(rawAmount * exchangeRate, "₭")}</span>`
          : "";

        // Exchange rate badge shown inline in meta row for foreign currencies
        const exchangeRateBadge = isForeign
          ? `<span style="background: rgba(251,191,36,0.15); color: #b45309; border: 1px solid rgba(251,191,36,0.35); border-radius: 8px; padding: 2px 8px; font-size: 0.78rem; font-weight: 600;">💱 1 ${currency} = ${formatCurrency(exchangeRate, "₭")}</span>`
          : "";

        return `
          <div class="transaction-item" style="${animationStyle}" data-row-index="${transaction.rowIndex}">
            <div class="transaction-header">
              <div class="transaction-title">${transaction.item || "N/A"}</div>
              <div class="transaction-amount ${amountClass}">
                ${amountDisplay}
                ${kipEquivalent}
              </div>
            </div>
            <div class="transaction-meta">
              <div class="transaction-info">
                <span>📍 ${transaction.category}</span>
                <span>📅 ${formatDateShort(transaction.date)}</span>
                <span>💳 ${transaction.accountType}</span>
                ${exchangeRateBadge}
              </div>
              <div style="display: flex; gap: 0.8rem; align-items: center; margin-left: auto;">
                ${projectBadge}
                ${documentLinks}
                <button class="btn-delete-transaction" data-row-index="${transaction.rowIndex}" data-item-name="${transaction.item || "N/A"}" title="ລຶບລາຍການ">
                  🗑️
                </button>
              </div>
            </div>
          </div>
        `;
      })
      .join("");

    // Load More button (only shown if there are more transactions)
    const loadMoreButton = hasMore
      ? `<button id="loadMoreBtn" class="btn btn-secondary" style="width: 100%; margin-top: 1.5rem; padding: 1rem; font-size: 1rem;">
           📥 ໂຫຼດເພີ່ມ (${totalTransactions - this.currentPage * this.pageSize} ລາຍການຍັງເຫຼือน)
         </button>`
      : "";

    // Show current page info
    const showingEnd = Math.min(
      this.currentPage * this.pageSize,
      totalTransactions,
    );

    return `
      <div class="glass-panel" style="padding: 2rem;">
        <div class="section-header" style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 2rem;">
          <h2 class="section-title" style="margin-bottom: 0;">📑 ລາຍການ</h2>
          <span style="font-weight: 700; color: var(--text-secondary); background: rgba(0,0,0,0.05); padding: 0.5rem 1rem; border-radius: 12px; font-size: 0.9rem;">
            ${showingEnd} / ${totalTransactions} ລາຍການ
          </span>
        </div>
        <div class="transaction-list-container" id="transactionListContainerInner">
          ${transactionItems}
        </div>
        ${loadMoreButton}
      </div>
    `;
  }

  /**
   * Render delete confirmation modal
   */
  renderDeleteModal() {
    return `
      <div id="deleteModal" class="modal-overlay" style="display: none;">
        <div class="modal-container">
          <div class="modal-header">
            <h3 class="modal-title">⚠️ ຢືນຢັນການລຶບ</h3>
          </div>
          <div class="modal-body">
            <p>ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການລຶບລາຍການນີ້?</p>
            <p id="deleteItemName" style="font-weight: 700; color: var(--text-primary); margin-top: 0.5rem;"></p>
            <p style="color: var(--accent-red); font-size: 0.9rem; margin-top: 1rem;">ການນຳການນີ້ບໍ່ສາມາດຍົກເລີກໄດ້!</p>
          </div>
          <div class="modal-footer">
            <button id="cancelDeleteBtn" class="modal-btn-cancel">ຍົກເລີກ</button>
            <button id="confirmDeleteBtn" class="modal-btn-confirm" style="background: var(--accent-red);">🗑️ ລຶບ</button>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Show delete confirmation modal
   */
  showDeleteModal(rowIndex, itemName) {
    const modal = document.getElementById("deleteModal");
    const deleteItemName = document.getElementById("deleteItemName");
    if (modal && deleteItemName) {
      deleteItemName.textContent = `"${itemName}"`;
      modal.style.display = "flex";
      // Store row index for confirmation
      modal.dataset.rowIndex = rowIndex;
    }
  }

  /**
   * Hide delete confirmation modal
   */
  hideDeleteModal() {
    const modal = document.getElementById("deleteModal");
    if (modal) {
      modal.style.display = "none";
      delete modal.dataset.rowIndex;
    }
  }

  /**
   * Handle delete transaction
   */
  async handleDeleteTransaction() {
    const modal = document.getElementById("deleteModal");
    if (!modal) return;

    const rowIndex = parseInt(modal.dataset.rowIndex);
    if (!rowIndex || rowIndex <= 1) {
      console.error("Invalid row index: ", rowIndex);
      alert("Invalid row index. Please try again.");
      return;
    }

    const confirmBtn = document.getElementById("confirmDeleteBtn");
    const cancelBtn = document.getElementById("cancelDeleteBtn");

    // Show loading state
    if (confirmBtn) {
      confirmBtn.disabled = true;
      confirmBtn.textContent = "ກຳລັງລຶບ...";
    }
    if (cancelBtn) {
      cancelBtn.disabled = true;
    }

    try {
      await googleSheetsService.deleteTransaction(rowIndex);
      this.hideDeleteModal();

      // Dispatch event to notify main.js to refresh data
      window.dispatchEvent(new CustomEvent("transactionDeleted"));
    } catch (error) {
      console.error("Error deleting transaction:", error);
      alert("ເກີດຂໍ້ຜິດພາດໃນການລຶບ: " + error.message);
    } finally {
      // Reset button states
      if (confirmBtn) {
        confirmBtn.disabled = false;
        confirmBtn.textContent = "🗑️ ລຶບ";
      }
      if (cancelBtn) {
        cancelBtn.disabled = false;
      }
    }
  }

  /**
   * Attach event handlers for transaction list delete buttons
   */
  attachTransactionListHandlers() {
    const deleteButtons = document.querySelectorAll(".btn-delete-transaction");
    deleteButtons.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        const rowIndex = parseInt(btn.dataset.rowIndex);
        const itemName = btn.dataset.itemName;
        this.showDeleteModal(rowIndex, itemName);
      });
    });
  }

  /**
   * Attach event handlers for delete confirmation modal
   * Should be called once when modal is rendered
   */
  attachModalHandlers() {
    const modal = document.getElementById("deleteModal");
    const cancelBtn = document.getElementById("cancelDeleteBtn");
    const confirmBtn = document.getElementById("confirmDeleteBtn");

    // Remove existing listeners by cloning (simple way to clear listeners)
    // or just rely on the fact that this is called once.
    // For safety against multiple calls, we can assume the caller handles lifecycle,
    // but looking at implementation, simply attaching is fine if called once.

    if (cancelBtn) {
      // Use clean event listeners approach if we were stricter,
      // but for now, we just attach.
      // To prevent stacking if called multiple times, we could removeEventListener first if we had named functions,
      // but splitting the method solves the main architectural issue.
      cancelBtn.addEventListener("click", () => this.hideDeleteModal());
    }

    if (confirmBtn) {
      confirmBtn.addEventListener("click", () =>
        this.handleDeleteTransaction(),
      );
    }

    // Close modal on overlay click
    if (modal) {
      modal.addEventListener("click", (e) => {
        if (e.target === modal) {
          this.hideDeleteModal();
        }
      });
    }

    // Close modal on Escape key
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        this.hideDeleteModal();
      }
    });
  }
}
